import type { FormEvent } from 'react';
import React from 'react';

import { DialogActions, FormField, FormHelp, Label, Textarea, TextButton } from '@zillow/constellation';

import type { Section } from '../../../models/index';
import YesAndNo from '../../Widgets/YesAndNo';
import { LEAD_BASED_PAINT_V2 } from '../../../../shared/constants';
import InlineTooltip from '../../Widgets/InlineTooltip';

type Props = {
  section: Section;
  setSectionValues: (keyValuePairs: object) => void;
};

export default function LeadBasedPaintHazards({ section, setSectionValues }: Props): JSX.Element {
  function onLeadPaintOnPropertyYesClick(): void {
    setSectionValues({
      [LEAD_BASED_PAINT_V2.FIELD_NAME.HAS_ON_PROPERTY]: true,
    });
  }

  function onLeadPaintOnPropertyNoClick(): void {
    setSectionValues({
      [LEAD_BASED_PAINT_V2.FIELD_NAME.HAS_ON_PROPERTY]: false,
      [LEAD_BASED_PAINT_V2.FIELD_NAME.HAS_RELATED_REPORTS]: null,
      [LEAD_BASED_PAINT_V2.FIELD_NAME.DESCRIPTION]: null,
    });
  }

  function onRelatedReportsYesClick(): void {
    setSectionValues({
      [LEAD_BASED_PAINT_V2.FIELD_NAME.HAS_RELATED_REPORTS]: true,
    });
  }

  function onRelatedReportsNoClick(): void {
    setSectionValues({
      [LEAD_BASED_PAINT_V2.FIELD_NAME.HAS_RELATED_REPORTS]: false,
    });
  }

  function onDescriptionChange(event: FormEvent<HTMLInputElement>): void {
    const { value } = event.currentTarget;

    setSectionValues({
      [LEAD_BASED_PAINT_V2.FIELD_NAME.DESCRIPTION]: value,
    });
  }

  function onApplicableChange(event: FormEvent<HTMLInputElement>): void {
    const { value } = event.currentTarget;

    setSectionValues({
      [LEAD_BASED_PAINT_V2.FIELD_NAME.REPORTS_DESCRIPTION]: value,
    });
  }

  const leadPaintOnProperty = section.getFieldValue(LEAD_BASED_PAINT_V2.FIELD_NAME.HAS_ON_PROPERTY);
  const relatedReports = section.getFieldValue(LEAD_BASED_PAINT_V2.FIELD_NAME.HAS_RELATED_REPORTS);

  return (
    <>
      <FormField
        label={
          <Label size="lg">
            Do you know of any{' '}
            <InlineTooltip
              tooltipBodyText={
                <>
                  Lead-based paint contains highly toxic metal that can cause serious health problems. Homes built
                  before 1978 (when lead based paint was banned) often contain lead based paint.
                  <DialogActions>
                    <TextButton
                      as="a"
                      target="_blank"
                      rel="noopener noreferrer"
                      href="https://www.epa.gov/lead/protect-your-family-exposures-lead"
                    >
                      Learn more
                    </TextButton>
                  </DialogActions>
                </>
              }
              closeWith={['blur']}
              openWith={['focus', 'click', 'mouseEnter']}
              shouldCloseOnOutsideClick
            >
              lead-based paint
            </InlineTooltip>{' '}
            on the property?
          </Label>
        }
        control={
          <YesAndNo
            yesHandler={onLeadPaintOnPropertyYesClick}
            noHandler={onLeadPaintOnPropertyNoClick}
            yesActive={leadPaintOnProperty}
          />
        }
      />
      {leadPaintOnProperty && (
        <>
          <FormField
            marginY="sm"
            label={<Label>Describe where on the property</Label>}
            control={
              <Textarea
                placeholder="(example: All doors and trim are assumed to have traces of lead paint.)"
                onChange={onDescriptionChange}
                value={section.getFieldValue(LEAD_BASED_PAINT_V2.FIELD_NAME.DESCRIPTION)}
                resize="vertical"
              />
            }
          />
          <FormField
            label={<Label>Do you know of any reports related to the property and lead-based paint?</Label>}
            control={
              <YesAndNo
                yesHandler={onRelatedReportsYesClick}
                noHandler={onRelatedReportsNoClick}
                yesActive={relatedReports}
              />
            }
          />
        </>
      )}
      {leadPaintOnProperty && relatedReports && (
        <FormField
          marginTop="sm"
          label={<Label>List all applicable report names</Label>}
          control={
            <Textarea
              onChange={onApplicableChange}
              value={section.getFieldValue(LEAD_BASED_PAINT_V2.FIELD_NAME.REPORTS_DESCRIPTION)}
              resize="vertical"
            />
          }
          description={
            <FormHelp>
              You must provide copies of the reports before the lease is signed by tenant; later on, you will have the
              ability to attach any reports to your lease
            </FormHelp>
          }
        />
      )}
    </>
  );
}
